import isEmpty from 'lodash/isEmpty'
import * as React from 'react'

import {
  default as ChildSection,
  initialParams as initialChildParams,
  validate as validateChild,
} from 'main/components/common/AuthModal/parent/IntermediateParentSignUpForm/ChildSection'
import {
  default as PasswordSection,
  initialParams as initialPasswordParams,
  validate as validatePassword,
} from 'main/components/common/AuthModal/parent/IntermediateParentSignUpForm/PasswordSection'
import { GatheringFields } from 'main/gql/gathering/'
import { REGISTRATION_QUERY } from 'main/gql/registration'
import { User_user } from 'main/gql/user'
import { Button, palette, spacing, Text } from 'main/ui'
import { formatDateInput } from 'main/util/date'
import { Mutation } from 'react-apollo'
import {
  CREATE_GATHERING_REGISTRATION_MUTATION,
  CreateGatheringRegistration,
  CreateGatheringRegistration_createGatheringRegistration,
  CreateGatheringRegistrationMutationFn,
  CreateGatheringRegistrationVariables,
} from '../../gql'
import {
  default as RegistrationSection,
  initialParams as initialRegistrationParams,
  validate as validateQuantity,
} from '../../RegistrationSection'
import { handleError } from './../../util'

interface IProps {
  user: User_user
  gathering: GatheringFields
  onComplete: (registration: CreateGatheringRegistration_createGatheringRegistration) => void
}

interface IChildParams {
  birthday: string
  desiredEnrollmentDate: string
}

interface IParams {
  childParams: IChildParams
  password: string
  quantity: number
}

interface IValidation {
  quantity?: string
  password?: string
  childParams?: {
    birthday?: string
    desiredEnrollmentDate?: string
  }
}

export default function Register(props: IProps) {
  const { user, onComplete, gathering } = props
  const collectPassword = !user.hasPassword
  const collectchildParams = user.family.children.length === 0

  const [params, setParams] = React.useState<IParams>({
    childParams: initialChildParams(),
    ...initialPasswordParams(),
    ...initialRegistrationParams(),
  })
  const [validationHints, setValidationHints] = React.useState<IValidation>({})
  const [error, setError] = React.useState<string | null>(null)

  return (
    <div>
      {error && <Text css={{ color: palette.rose }}>{error}</Text>}

      <RegistrationSection
        validation={{ quantity: validationHints.quantity }}
        params={{ quantity: params.quantity }}
        onChange={quantity => handleChange(quantity, setParams)}
      />
      {renderPasswordMaybe()}
      {renderChildMaybe()}

      <Mutation<CreateGatheringRegistration, CreateGatheringRegistrationVariables>
        mutation={CREATE_GATHERING_REGISTRATION_MUTATION}
        onCompleted={data => {
          if (data.createGatheringRegistration) onComplete(data.createGatheringRegistration)
        }}
        onError={apolloError => handleError(apolloError, setError)}
        refetchQueries={[{ query: REGISTRATION_QUERY, variables: { gatheringId: gathering.id } }]}
      >
        {(mutate, { loading }) => (
          <Button
            css={{ marginTop: spacing.majorScale(4) }}
            disabled={loading}
            onClick={() =>
              handleSubmit(
                gathering.id,
                params,
                collectPassword,
                collectchildParams,
                setValidationHints,
                setError,
                mutate,
              )
            }
            full
            data-pendo="eventRsvpButton"
          >
            Register your RSVP
          </Button>
        )}
      </Mutation>
    </div>
  )

  function renderPasswordMaybe() {
    if (!collectPassword) return null

    return (
      <div css={{ marginTop: spacing.majorScale(2) }}>
        <PasswordSection
          params={{ password: params.password }}
          validation={{ password: validationHints.password }}
          onChange={password => handleChange(password, setParams)}
        />
      </div>
    )
  }

  function renderChildMaybe() {
    if (!collectchildParams) return null

    const data = params.childParams

    return (
      <div css={{ marginTop: spacing.majorScale(2) }}>
        <ChildSection
          params={params.childParams}
          validation={validationHints.childParams || {}}
          onChange={changes => {
            const childParams = { ...data, ...changes }
            handleChange({ childParams }, setParams)
          }}
        />
      </div>
    )
  }
}

function handleChange(
  changes: Partial<IParams>,
  setParams: React.Dispatch<React.SetStateAction<IParams>>,
) {
  setParams(params => ({ ...params, ...changes }))
}

function handleSubmit(
  gatheringId: number,
  params: IParams,
  collectPassword: boolean,
  collectchildParams: boolean,
  setValidationHints: React.Dispatch<React.SetStateAction<IValidation>>,
  setError: (error: string | null) => void,
  mutate: CreateGatheringRegistrationMutationFn,
) {
  setError(null)
  const validationHints = validateParams(params, !collectchildParams, !collectPassword)

  setValidationHints(validationHints)

  if (!isEmpty(validationHints)) return

  let variables: CreateGatheringRegistrationVariables = {
    gatheringId,
    gatheringRegistration: { quantity: params.quantity },
  }
  variables = collectPassword ? { ...variables, password: params.password } : variables
  variables = collectchildParams
    ? { ...variables, child: formatChildParamsDates(params.childParams) }
    : variables
  mutate({ variables })
}

function validateParams(params: IParams, skipchildParams: boolean, skipPassword: boolean) {
  let validation: IValidation = {}

  const quantityValidation = validateQuantity({ quantity: params.quantity })

  if (quantityValidation) validation = { ...validation, ...quantityValidation }

  if (!skipPassword) {
    validation = { ...validation, ...validatePassword({ password: params.password }) }
  }

  if (!skipchildParams) {
    const childValidation = validateChild(params.childParams)

    if (Object.keys(childValidation).length) {
      validation = { ...validation, childParams: childValidation }
    }
  }

  return validation
}

function formatChildParamsDates(childParams: IChildParams) {
  if (!childParams) return null

  return {
    ...childParams,
    birthday: formatDateInput(childParams.birthday),
    desiredEnrollmentDate: formatDateInput(childParams.desiredEnrollmentDate),
  }
}
